import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { IGif } from "@gtmhub/comments";
import { localize } from "@gtmhub/localization";
import { SizeOfGif } from "@gtmhub/shared/services/tenor-gif.service";
import { IdMap } from "@gtmhub/util";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { AssigneesFacade } from "@webapp/assignees/services/assignees/assignees-facade.service";
import { Comment, CommentTarget, UiComment } from "@webapp/comments/models/comments.models";
import { isGifOrEmojiPickerSearchFieldSelected } from "@webapp/shared/rich-text-editor/rich-text-editor.utils";

@Component({
  selector: "comment",
  templateUrl: "./comment.component.html",
  styleUrls: ["./comment.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentComponent implements OnChanges {
  @Input() public comment: UiComment;
  @Input() public isEdited = false;
  @Input() public target: CommentTarget;
  @Input() public metadata: { [key: string]: string };
  @Input() public canCurrentUserModifyComment = false;
  /** hides the reactions and mentions when set to true, defaults to false */
  @Input() public hideCommentMeta = false;

  @Output() public readonly edit: EventEmitter<Comment> = new EventEmitter();
  @Output() public readonly editorClose: EventEmitter<void> = new EventEmitter();

  @Output() public readonly editClick: EventEmitter<void> = new EventEmitter();
  @Output() public readonly deleteClick: EventEmitter<void> = new EventEmitter();

  public assignees: IdMap<Assignee>;
  public sizeOfGif = SizeOfGif.Big;
  public mentioned: string[] = [];

  private originalCommentContent: {
    text: string;
    gif: IGif;
  };

  public constructor(assigneesFacade: AssigneesFacade, private changeDetector: ChangeDetectorRef) {
    this.assignees = assigneesFacade.getAssigneesIdMap();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.comment) {
      this.initMentionedUsers();
    }
  }

  public getMentionedMetaTooltipTitle(mentionId: string): string {
    const userWasNotified = `${this.assignees[mentionId].name} ${localize("was_notified")}`;
    const seen = this.comment.seen?.includes(mentionId) ? ` (${localize("seen")})` : "";

    return `${userWasNotified}${seen}`;
  }

  public handleCommentUpdate(comment: Comment): void {
    Object.assign(this.comment, comment);
    this.initMentionedUsers();
    this.edit.emit(comment);
  }

  public handleEditorOpenClick(): void {
    this.originalCommentContent = {
      text: this.comment.text,
      gif: { ...this.comment.gif },
    };

    this.editClick.emit();
  }

  public handleContentBlur(): void {
    if (isGifOrEmojiPickerSearchFieldSelected(document.activeElement)) {
      return;
    }

    window.setTimeoutOutsideAngular(() => {
      this.comment.text = this.originalCommentContent?.text;
      this.comment.gif = this.originalCommentContent?.gif;

      this.changeDetector.detectChanges();
    });

    this.editorClose.emit();
  }

  private initMentionedUsers(): void {
    this.mentioned = this.comment?.mentioned?.filter((mentionId) => this.assignees[mentionId] != null) || [];
  }
}
